// ClientHub shell-surface data layer (Epic 4 · 01_SHELL_CHARLES.md).
//
// The shell-level surfaces — inbox, mentions, global search — read through here so
// no shell component imports the Supabase client directly (00_CONTRACT.md §11: one
// wrapper per source, components never touch supabase). The running timer lives
// in `api/hub/effort.js` (fn_timer_start / fn_timer_stop) and is consumed through the
// shell's TimerContext, not from here.
//
// PROBE STATUS (2026-09-07): `fn_my_mentions` is confirmed to EXIST on the live
// Supabase (docs/CLIENTHUB-RPC-GAPS.md §1). `v_my_messages`, `fn_mention_seen`
// and `fn_work_search` are NOT in the confirmed set — they may be missing. Every
// wrapper throws on a Postgres error so the surface degrades through <ErrorState>
// (never a silent zero/empty). Confirm each name/shape against the live DB before
// relying on it; the names/params below follow the spec's declared contract.

import { supabase } from '../lib/supabase';

const wrap = (data) => ({ error: false, data, message: 'ok' });

// ─── Inbox (S4.1) ─────────────────────────────────────────────────────────────
// v_my_messages is the one-store view: one row per message across every channel
// (email, chat, DM, task comment, design feedback). The wall is IN the view — RLS
// already filtered what this person may see, so a UI mistake cannot leak a DM. We
// read a recent page; the drawer paginates by refetching a larger window.
//
// CONFIRMED column (live probe 2026-09-14): the canonical message-time column is
// `message_timestamp` (timestamptz). There is NO `at` column — ordering by `at`
// raised "column v_my_messages.at does not exist". Other timestamps (`sent_at`,
// `created_at`) exist but can be null for received/draft rows, so `message_timestamp`
// is the reliable sort key. The drawer reads fields defensively
// (m.message_timestamp || m.sent_at || m.created_at || …).
export async function getMyMessages({ limit = 200 } = {}) {
  const { data, error } = await supabase
    .from('v_my_messages')
    .select('*')
    .order('message_timestamp', { ascending: false })
    .limit(limit);
  if (error) throw { message: `inbox: ${error.message}` };
  return wrap(Array.isArray(data) ? data : []);
}

// ─── Mentions (S4.3) ──────────────────────────────────────────────────────────
// fn_my_mentions(p_unseen_only boolean default true) -> jsonb array. Each:
//   { id, at, by, client, says, thread_id, task_id, task, seen }
// Confirmed to EXIST (RPC-GAPS §1); the return shape needs a signed-in probe pass.
export async function getMyMentions(unseenOnly = true) {
  const { data, error } = await supabase.rpc('fn_my_mentions', {
    p_unseen_only: unseenOnly,
  });
  if (error) throw { message: `mentions: ${error.message}` };
  return wrap(Array.isArray(data) ? data : []);
}

// Mark a mention seen (or all when id omitted). Returns the count updated.
export async function markMentionSeen(id = null) {
  const params = id != null ? { p_id: id } : {};
  const { data, error } = await supabase.rpc('fn_mention_seen', params);
  if (error) throw { message: `mention seen: ${error.message}` };
  return wrap(data);
}

// ─── Global search (S4.4) ─────────────────────────────────────────────────────
// fn_work_search(p_q text, p_limit int default 40) -> jsonb. Searches work AND
// conversation, five years, permission-filtered in SQL (walls apply through
// v_my_messages). Never leaks a gated result.
export async function workSearch(q, limit = 40) {
  const { data, error } = await supabase.rpc('fn_work_search', {
    p_q: q,
    p_limit: limit,
  });
  if (error) throw { message: `search: ${error.message}` };
  return wrap(Array.isArray(data) ? data : []);
}
